/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "components/DataTable";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputActionMeta } from "react-select";
import apiService from "services/apiService";
import { sortByKey } from "util/sortOptions";
import { AiOutlineExport } from "react-icons/ai";
import CommonListingFilter, {
  CommonFilterField,
  CommonFilterOption,
} from "components/CommonListingFilter/CommonListingFilter";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "contexts";
import { BannerContext } from "components/SideBar";
//merged with testing branch to resolve conflicts
interface OptionProps {
  value: string;
  label: string;
}

const ALL_USERS_OPTION: OptionProps = { value: "", label: "All Users" };

type UserOptionSource = { email?: string; displayName?: string };

interface IProps {
  data: any;
  columns: any;
  itemsPerPage?: number;
  title?: string;
  loading?: boolean;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onApply?: (filters: any) => void;
  statusList?: { value: string; label: string }[];
  typeList?: { value: string; label: string }[];
  userList?: { value: string; label: string }[];
  isShowFilter?: boolean;
  customButton?: React.ReactNode;
  buttonContent?: string;
  currentFilters?: {
    username: string;
    status: string;
    type: string;
    fromDate: string;
    toDate: string;
  };
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  onSizeChange?: (size: number) => void;
  onSearchTermChange?: (searchTerm: string) => void;
}

const ReimbursementList: React.FC<IProps> = ({
  data,
  columns,
  loading,
  onSuccess,
  onError,
  onApply,
  statusList,
  typeList,
  userList,
  customButton,
  buttonContent,
  currentFilters,
  page = 0,
  size = 10,
  totalElements = 0,
  onPageChange,
  onSizeChange,
  onSearchTermChange,
}) => {
  const [userOptions, setUserOptions] = useState<OptionProps[]>([]);
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const USER_PAGE_SIZE = 10;
  const [localFilters, setLocalFilters] = useState({
    username: "",
    status: "",
    type: "",
    fromDate: "",
    toDate: "",
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAppContext();
  console.log("ReimbursementList user", user);
  const userDetail: any = localStorage.getItem("user");
  console.log("ReimbursementList userDetail", userDetail);
  console.log("ReimbursementList onSuccess", onSuccess);
  const { userRole } = useAppContext();

    const { isBannerVisible } = useContext(BannerContext);
  
    const dynamicHeightClass = isBannerVisible
      ? "h-[calc(100vh-108px)] min-h-[300px]"
      : "h-[calc(100vh-80px)] min-h-[300px]";

  const getUsersFromPayload = (payload: unknown): UserOptionSource[] => {
    if (Array.isArray(payload)) {
      return payload as UserOptionSource[];
    }

    if (
      typeof payload === "object" &&
      payload !== null &&
      "content" in payload &&
      Array.isArray((payload as { content?: unknown }).content)
    ) {
      return (payload as { content: UserOptionSource[] }).content;
    }

    return [];
  };

  useEffect(() => {
    if (currentFilters) {
      setLocalFilters(currentFilters);
    }
  }, [currentFilters]);

  const fetchUsers = async (
    nextPage: number,
    searchValue: string,
    append: boolean
  ) => {
    if (loadingUsers) return;

    setLoadingUsers(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(nextPage));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) {
        query.set("username", searchValue.trim());
      }

      const res = await apiService.get(`api/users?${query.toString()}`);
      const payload = res.data;
      const users = getUsersFromPayload(payload);

      const fetchedOptions = users
        .filter((item: any) => item?.email)
        .map((item: any) => ({
          value: item.displayName,
          label: item.displayName || item.email,
        }));

      setUserOptions((prev) => {
        const base = append ? [...prev, ...fetchedOptions] : fetchedOptions;
        const fallback = (userList || []).map((opt) => ({
          value: opt.value,
          label: opt.label,
        }));
        const merged = [...base, ...fallback];
        const dedup = new Map<string, OptionProps>();
        merged.forEach((opt) => {
          if (!dedup.has(opt.value)) {
            dedup.set(opt.value, opt);
          }
        });
        return sortByKey(Array.from(dedup.values()), "label");
      });

      setUserPage(nextPage);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(nextPage < payload.totalPages - 1);
      } else {
        setHasMoreUsers(fetchedOptions.length === USER_PAGE_SIZE);
      }
    } catch (err) {
      console.log("Failed to fetch users", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (user?.role === 1) { return; } // Skip fetching users for non-admins
    const debounceTimer = window.setTimeout(() => {
      fetchUsers(0, userSearch, false);
    }, 350);

    return () => window.clearTimeout(debounceTimer);
  }, [userSearch, user]);

  const selectedUserOption = useMemo(() => {
    if (!localFilters.username) return ALL_USERS_OPTION;

    const selectedFromOptions = userOptions.find(
      (opt) => opt.value === localFilters.username
    );
    if (selectedFromOptions) return selectedFromOptions;

    const selectedFromFallback = (userList || []).find(
      (opt) => opt.value === localFilters.username
    );
    if (selectedFromFallback) {
      return {
        value: selectedFromFallback.value,
        label: selectedFromFallback.label,
      };
    }

    return { value: localFilters.username, label: localFilters.username };
  }, [localFilters.username, userList, userOptions]);

  const handleUserSearch = (inputValue: string, meta: InputActionMeta) => {
    if (meta.action === "input-change") {
      setUserSearch(inputValue);
      return inputValue;
    }

    if (meta.action === "menu-close") {
      setUserSearch("");
      return "";
    }

    return inputValue;
  };

  const loadMoreUsers = () => {
    if (hasMoreUsers && !loadingUsers) {
      fetchUsers(userPage + 1, userSearch, true);
    }
  };

  const handleAddBtnClick = () => {
    navigate("/admin/reimbursement/add");
  };

  const applyFilters = (nextFilters: typeof localFilters) => {
    if (onApply && JSON.stringify(nextFilters) !== JSON.stringify(currentFilters)) {
      onApply(nextFilters);
    }
  };

  const updateLocalFilter = (key: keyof typeof localFilters, value: string) => {
    setLocalFilters((prev) => {
      const next = { ...prev, [key]: value };

      // If fromDate is cleared, reset toDate to keep date dependency valid.
      if (key === "fromDate" && !value) {
        next.toDate = "";
      }
      return next;
    });
  };

  const getClearedFilters = () => ({
    username: "",
    status: "",
    type: "",
    fromDate: "",
    toDate: "",
  });

  const handleClearFilters = () => {
    setLocalFilters(getClearedFilters());
  };

  const handleResetFilters = () => {
    const cleared = getClearedFilters();
    setLocalFilters(cleared);
    applyFilters(cleared);
  };

  const handleApplyFilters = () => {
    applyFilters(localFilters);
  };

  const handleExport = async () => {
    if (isExporting) return;

    const appliedFilters = currentFilters || localFilters;
    const query = new URLSearchParams();

    if (appliedFilters.username) {
      query.set("username", appliedFilters.username);
    }
    if (appliedFilters.status) {
      query.set("status", appliedFilters.status);
    }
    if (appliedFilters.fromDate) {
      query.set("fromDate", appliedFilters.fromDate);
    }
    if (appliedFilters.toDate) {
      query.set("toDate", appliedFilters.toDate);
    }
    if (appliedFilters.type) {
      query.set("type", appliedFilters.type);
    }

    setIsExporting(true);
    try {
      const url = query.toString()
        ? `/api/reimbursement/export?${query.toString()}`
        : "/api/reimbursement/export";

      const response = await apiService.get(url, { responseType: "blob" });
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], {
            type: "application/vnd.ms-excel",
          });

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `reimbursement-export-${new Date()
        .toISOString()
        .slice(0, 10)}.xls`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.log("Failed to export reimbursement", error);
      onError("Failed to export reimbursement data");
    } finally {
      setIsExporting(false);
    }
  };

  const todayStr = new Date().toISOString().split("T")[0];

  const reimbursementFilterFields: CommonFilterField[] = [
    ...(userRole !== 1
      ? [
        {
          id: "reimbursement-user",
          type: "select",
          label: "Employee",
          placeholder: "All Users",
          widthClass: "min-w-[220px]",
          queryParam: "username",
          options: [ALL_USERS_OPTION, ...userOptions] as CommonFilterOption[],
          value: selectedUserOption as CommonFilterOption | null,
          isClearable: Boolean(localFilters.username),
          isSearchable: true,
          isLoading: loadingUsers,
          onInputChange: handleUserSearch,
          onMenuScrollToBottom: loadMoreUsers,
          noOptionsMessage: () =>
            loadingUsers ? "Loading users..." : "No users found",
          onChange: (selectedOption) =>
            updateLocalFilter("username", selectedOption?.value || ""),
        } as CommonFilterField,
      ] : []),

    {
      id: "reimbursement-type",
      type: "native-select",
      label: "Type",
      widthClass: "min-w-[140px]",
      queryParam: "type",
      value: localFilters.type,
      options: [
        { value: "", label: t("All") },
        ...((typeList || []).map((opt) => ({
          value: opt.value,
          label: opt.label,
        })) as CommonFilterOption[]),
      ],
      onChange: (value) => updateLocalFilter("type", value),
    },
    {
      id: "reimbursement-status",
      type: "native-select",
      label: "Status",
      widthClass: "min-w-[140px]",
      queryParam: "status",
      value: localFilters.status,
      options: [
        { value: "", label: "All" },
        ...((statusList || []).map((opt) => ({
          value: opt.value,
          label: opt.label,
        })) as CommonFilterOption[]),
      ],
      onChange: (value) => updateLocalFilter("status", value),
    },
    {
      id: "reimbursement-from-date",
      type: "date",
      label: "From Date",
      widthClass: "min-w-[170px]",
      queryParam: "fromDate",
      value: localFilters.fromDate,
      max: todayStr,
      onChange: (value) => updateLocalFilter("fromDate", value),
    },
    {
      id: "reimbursement-to-date",
      type: "date",
      label: "To Date",
      widthClass: "min-w-[170px]",
      queryParam: "toDate",
      value: localFilters.toDate,
      min: localFilters.fromDate || undefined,
      max: todayStr,
      disabled: !localFilters.fromDate,
      onChange: (value) => updateLocalFilter("toDate", value),
    },
  ];

  return (
    <>
      {data && (
        <DataTable
          heightClass={dynamicHeightClass}
          data={data}
          columns={columns}
          onAddBtnClick={handleAddBtnClick}
          loading={loading}
          itemsPerPage={size}
          searchWidth="w-48 lg:w-52 xl:w-56"
          actionsContainerClassName="ml-auto flex items-center space-x-2 sm:space-x-3"
          serverSidePagination={true}
          currentPage={page + 1}
          totalItems={totalElements}
          onPageChange={(p) => onPageChange?.(p - 1)}
          onItemsPerPageChange={(s) => { onSizeChange?.(s); }}
          onSearchTermChange={onSearchTermChange}
          searchDebounceMs={500}
          leftSideButtons={
            <CommonListingFilter
              className="reimbursement-listing-filter"
              fields={reimbursementFilterFields}
              panelTitle="Reimbursement Filter"
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
              onReset={handleResetFilters}
            />
          }
          preAddButtons={
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className="text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm font-medium rounded-lg text-sm px-4 py-2.5 inline-flex items-center border-radius-0.5rem"
            >
              <AiOutlineExport size={18} className="mr-1.5" />
              {isExporting ? "Exporting..." : t("Export")}
            </button>
          }
          customButton={customButton}
          buttonContent={buttonContent}
          headerLabelClassName="font-medium dark:bg-gray-800"
          hasActiveFilters={
            !!(localFilters.username || localFilters.status || localFilters.type || localFilters.fromDate || localFilters.toDate)
          }
        />
      )}
    </>
  );
};
export default ReimbursementList;
